// Notification service for managing real-time notifications
import { API_CONFIG } from '../constants/config';
import webSocketService, { type Notification } from './webSocketService';

export class NotificationService {
  private notifications: Notification[] = [];
  private listeners: ((notifications: Notification[]) => void)[] = [];
  private unsubscribeWebSocket: (() => void) | null = null;

  constructor() {
    this.initializeWebSocketListener();
    this.loadExistingNotifications();
  }

  private initializeWebSocketListener(): void {
    // Listen for new notifications from WebSocket using the new observable pattern
    const subscription = webSocketService.notifications().subscribe({
      next: (notification: Notification) => {

        // Backend sends LocalDateTime without timezone - treat as UTC
        const processedNotification = {
          ...notification,
          createdAt: notification.createdAt && !notification.createdAt.endsWith('Z')
            ? new Date(`${notification.createdAt}Z`).toISOString()
            : notification.createdAt || new Date().toISOString(),
        };

        // Add to local store
        this.addNotification(processedNotification);

        // Show toast notification if needed
        this.showToastNotification(processedNotification);
      },

    });

    // Store unsubscribe function
    this.unsubscribeWebSocket = () => subscription.unsubscribe();
  }

  private async loadExistingNotifications(): Promise<void> {
    try {
      // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
      // In production, use the full backend URL
      const isDev = import.meta.env.DEV;
      const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://grandpittu-backend.onrender.com');
      const url = `${baseUrl}${API_CONFIG.SLUG}/notification/getAllByPeriod`;

      const response = await fetch(url, {
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const rawNotifications = await response.json();
          // Backend LocalDateTime format: "2024-01-15T10:30:00" - parse properly
          const notifications = (rawNotifications || []).map((notification: Notification) => ({
            ...notification,
            createdAt: notification.createdAt ?
              new Date(notification.createdAt.endsWith('Z') ? notification.createdAt : `${notification.createdAt}Z`).toISOString() :
              new Date().toISOString()
          }));
          this.notifications = notifications;
          this.notifyListeners();
        } else {
          console.warn('⚠️ Backend returned HTML instead of JSON -  server error');
        }
      } else {
        console.warn(`⚠️ Notification service unavailable`);
      }
    } catch (error) {
      console.warn('⚠️ Could not load notifications - backend may be unavailable:', error);
    }
  }

  private addNotification(notification: Notification): void {
    // Add to the beginning of the array (newest first)
    this.notifications.unshift(notification);

    // Keep only the latest 50 notifications to prevent memory issues
    if (this.notifications.length > 50) {
      this.notifications = this.notifications.slice(0, 50);
    }

    this.notifyListeners();
  }

  private showToastNotification(notification: Notification): void {
    // Only show toast for important notification types
    if (notification.type === 'NEW_BOOKING') {
      // Dynamic import to avoid circular dependency issues
      import('react-hot-toast').then(({ default: toast }) => {
        toast.success(notification.message, {
          duration: 5000,
          icon: '🔔',
        });
      });
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener([...this.notifications]);
      } catch (error) {
      }
    });
  }

  // Public API
  public getNotifications(): Notification[] {
    return [...this.notifications];
  }

  public getUnreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  public async markAsRead(notificationId: number): Promise<void> {
    const notification = this.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.isRead = true;
      this.notifyListeners();

      // Send to backend to persist read status
      try {
        // In development, use relative URLs to leverage Vite proxy
        const isDev = import.meta.env.DEV;
        const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://grandpittu-backend.onrender.com');
        const url = `${baseUrl}${API_CONFIG.SLUG}/notification/${notificationId}`;

        await fetch(url, {
          method: 'POST',
          // Include credentials (cookies) for HTTP-only cookie auth
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        // Don't revert local state - user experience is more important
      }
    }
  }

  public markAllAsRead(): void {
    this.notifications.forEach(n => n.isRead = true);
    this.notifyListeners();
  }

  public clearNotifications(): void {
    this.notifications = [];
    this.notifyListeners();
  }

  public subscribe(callback: (notifications: Notification[]) => void): () => void {
    this.listeners.push(callback);

    // Immediately call with current notifications
    callback([...this.notifications]);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  public destroy(): void {
    if (this.unsubscribeWebSocket) {
      this.unsubscribeWebSocket();
      this.unsubscribeWebSocket = null;
    }
    this.listeners = [];
    this.notifications = [];
  }
}

// Create singleton instance
export const notificationService = new NotificationService();
export default notificationService;